import { EditOutlined, EllipsisOutlined } from '@ant-design/icons'
import { Button, Dropdown, Row, Space, Table } from 'antd'
import { type TableColumnsType as ColumnsType } from 'antd'
import { useMemo, type FunctionComponent } from 'react'
import { Link } from 'react-router-dom'
import { LocalDateTime, LocalTimeZone } from '~/components/LocalDateTime'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { useUrlQuery } from '~/hooks/useUrlParams'
import { type Course } from '~/types/course'
import { compareDate, compareNumber, compareText } from '~/utils/helpers'

interface CourseTableProps {
  data: Course[]
  loading: boolean
  duplicateCourse: (id: number) => void
  confirmDelete: (id: number, name: string) => void
  setOpenAssignUserModel: (confirm: boolean) => void
  setSelectedCourse: (course: Course) => void
}

const CourseTable: FunctionComponent<CourseTableProps> = ({
  data,
  loading,
  duplicateCourse,
  confirmDelete,
  setOpenAssignUserModel,
  setSelectedCourse
}: CourseTableProps) => {
  const datasource: Course[] = useTableDataSourceKey(data)
  const { queryValue, setQueryValue } = useUrlQuery({ name: 'course-filter' })

  const columns: ColumnsType<Course> = useMemo((): ColumnsType<Course> => (
    [
      {
        title: 'ID',
        dataIndex: 'id',
        sorter: (a, b) => compareNumber(a.id, b.id)
      },
      {
        title: 'Title',
        render: (_, course) => <Link to={`${course.id}`}>{course.name}</Link>,
        sorter: (a, b) => compareText(a.name, b.name),
        defaultFilteredValue: queryValue,
        filters: [
          { text: 1, value: 1 }, // ISP
          { text: 2, value: 2 }, // CVI
          { text: 3, value: 3 } // CAAS
        ],
        onFilter: (value, record) => {
          return record.software?.id === value
        }
      },
      {
        title: 'Price',
        render: (_, course) => `${course.currency ?? 'USD'} ${course.registration_fee}`,
        sorter: (a, b) => compareNumber(a.registration_fee, b.registration_fee)
      },
      {
        title: 'Start date',
        render: (_, course) => <LocalDateTime date={course.start_at} timezone={course.timezone} />,
        sorter: (a, b) => compareDate(a.start_at, b.start_at)
      },
      {
        title: 'End date',
        render: (_, course) => <LocalDateTime date={course.end_at} timezone={course.timezone} />,
        sorter: (a, b) => compareDate(a.end_at, b.end_at)
      },
      {
        title: 'Time zone',
        render: (_, course) => <LocalTimeZone timezone={course.timezone} />
      },
      {
        title: 'Cases',
        render: (_, course) => {
          return course?.uploaded_cases.length
        }
      },
      {
        title: 'Workstations',
        render: (_, course) => {
          return course.participants?.length ?? 0
        }
      },
      {
        title: 'Actions',
        render: (_, course) => {
          return (
            <Space size="middle">
              <Link to={`edit/${course.id}`}>
                <Button type="primary" icon={<EditOutlined />} />
              </Link>
              <Dropdown placement="bottom" trigger={['click']} menu={{
                items: [
                  {
                    key: 'duplicate',
                    label: 'Duplicate',
                    onClick: () => { duplicateCourse(course.id) }
                  },
                  {
                    key: 'delete',
                    label: 'Delete',
                    onClick: () => { confirmDelete(course.id, course.name) }
                  },
                  {
                    key: 'assign_user',
                    label: 'Add to user',
                    onClick: () => {
                      setOpenAssignUserModel(true)
                      setSelectedCourse(course)
                    }
                  },
                  { key: 'cases', label: <Link to={`${course.id}/cases`}>Cases</Link> },
                  { key: 'workstations', label: <Link to={`${course.id}/workstations`}>Workstations</Link> }
                ]
              }}>
                <Button type="primary" icon={<EllipsisOutlined />} />
              </Dropdown>
            </Space>
          )
        }
      }
    ]
  ), [confirmDelete, duplicateCourse, queryValue, setOpenAssignUserModel, setSelectedCourse])

  return <Table
    size="small"
    tableLayout="auto"
    dataSource={datasource}
    loading={loading}
    footer={() => (
      <Row justify="start">
        <span>Total: {datasource.length} courses</span>
      </Row>
    )}
    columns={columns}
    onChange={(p, f) => {
      const titleColPosition = 1
      const searchValues = (f[titleColPosition] ?? []) as string[]
      setQueryValue(searchValues)
    }}
  />
}

export default CourseTable
